import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, Search, Edit2, Trash2, Calendar, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReservationsPage = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const [reservations, setReservations] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [formData, setFormData] = useState({
    unit_id: '',
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    check_in_date: '',
    check_out_date: '',
    total_amount: '',
    advance_payment: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reservationsRes, unitsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reservations`, { withCredentials: true }),
        axios.get(`${API_URL}/api/units`, { withCredentials: true })
      ]);
      setReservations(reservationsRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        total_amount: parseFloat(formData.total_amount),
        advance_payment: formData.advance_payment ? parseFloat(formData.advance_payment) : 0
      };

      if (editingReservation) {
        await axios.put(`${API_URL}/api/reservations/${editingReservation.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/reservations`, payload, { withCredentials: true });
      }
      toast.success(t('saveSuccess'));
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleEdit = (reservation) => {
    setEditingReservation(reservation);
    setFormData({
      unit_id: reservation.unit_id || '',
      guest_name: reservation.guest_name || '',
      guest_phone: reservation.guest_phone || '',
      guest_email: reservation.guest_email || '',
      check_in_date: reservation.check_in_date || '',
      check_out_date: reservation.check_out_date || '',
      total_amount: reservation.total_amount?.toString() || '',
      advance_payment: reservation.advance_payment?.toString() || '',
      notes: reservation.notes || ''
    });
    setIsModalOpen(true);
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/reservations/${id}`, { status: newStatus }, { withCredentials: true });
      toast.success(t('saveSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/reservations/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setEditingReservation(null);
    setFormData({
      unit_id: '',
      guest_name: '',
      guest_phone: '',
      guest_email: '',
      check_in_date: '',
      check_out_date: '',
      total_amount: '',
      advance_payment: '',
      notes: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      reserved: 'bg-amber-50 text-amber-700 border-amber-600',
      checked_in: 'bg-emerald-50 text-emerald-700 border-emerald-600',
      checked_out: 'bg-blue-50 text-blue-700 border-blue-600',
      cancelled: 'bg-red-50 text-red-700 border-red-600'
    };
    return `px-2 py-1 text-xs font-bold uppercase border ${styles[status] || 'bg-gray-100'}`;
  };

  const getUnitName = (unitId) => units.find(u => u.id === unitId)?.name || '-';

  const filteredReservations = reservations.filter(r => 
    statusFilter === 'all' || r.status === statusFilter
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reservations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('reservations')}</h1>
          <p className="text-gray-500 text-sm mt-1">{reservations.length} {t('total')}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-reservation-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addReservation')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-none" data-testid="reservation-status-filter">
            <SelectValue placeholder={t('status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="reserved">{t('reserved')}</SelectItem>
            <SelectItem value="checked_in">{t('checkedIn')}</SelectItem>
            <SelectItem value="checked_out">{t('checkedOut')}</SelectItem>
            <SelectItem value="cancelled">{t('cancelled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reservations Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('units')}</th>
              <th>{t('guestName')}</th>
              <th>{t('checkInDate')}</th>
              <th>{t('checkOutDate')}</th>
              <th>{t('totalAmount')}</th>
              <th>{t('remainingBalance')}</th>
              <th>{t('status')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredReservations.length > 0 ? (
              filteredReservations.map((reservation) => (
                <tr key={reservation.id} data-testid={`reservation-row-${reservation.id}`}>
                  <td className="font-medium">{getUnitName(reservation.unit_id)}</td>
                  <td>{reservation.guest_name}</td>
                  <td>{reservation.check_in_date?.split('T')[0]}</td>
                  <td>{reservation.check_out_date?.split('T')[0]}</td>
                  <td className="font-mono">${reservation.total_amount}</td>
                  <td className="font-mono text-red-600">${reservation.remaining_balance}</td>
                  <td>
                    <span className={getStatusBadge(reservation.status)}>
                      {reservation.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      {reservation.status === 'reserved' && (
                        <button 
                          onClick={() => handleStatusChange(reservation.id, 'checked_in')} 
                          className="p-1 hover:bg-emerald-100 text-emerald-600"
                          title="Check In"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {reservation.status === 'checked_in' && (
                        <button 
                          onClick={() => handleStatusChange(reservation.id, 'checked_out')} 
                          className="p-1 hover:bg-blue-100 text-blue-600"
                          title="Check Out"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleEdit(reservation)} 
                        className="p-1 hover:bg-gray-100"
                        data-testid={`edit-reservation-${reservation.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(reservation.id)} 
                        className="p-1 hover:bg-red-100 text-red-600"
                        data-testid={`delete-reservation-${reservation.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-8 text-gray-500">
                  {t('noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {editingReservation ? t('editReservation') : t('addReservation')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('units')} *</label>
              <Select 
                value={formData.unit_id} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, unit_id: val }))}
                disabled={!!editingReservation}
              >
                <SelectTrigger className="rounded-none mt-1" data-testid="reservation-unit-select">
                  <SelectValue placeholder={t('selectUnit')} />
                </SelectTrigger>
                <SelectContent>
                  {units.filter(u => u.status === 'vacant' || u.id === formData.unit_id).map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('guestName')} *</label>
                <Input
                  value={formData.guest_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_name: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('guestPhone')} *</label>
                <Input
                  value={formData.guest_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, guest_phone: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('guestEmail')}</label>
              <Input
                type="email"
                value={formData.guest_email}
                onChange={(e) => setFormData(prev => ({ ...prev, guest_email: e.target.value }))}
                className="rounded-none mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('checkInDate')} *</label>
                <Input
                  type="date"
                  value={formData.check_in_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, check_in_date: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('checkOutDate')} *</label>
                <Input
                  type="date"
                  value={formData.check_out_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, check_out_date: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('totalAmount')} *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.total_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, total_amount: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('advancePayment')}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.advance_payment}
                  onChange={(e) => setFormData(prev => ({ ...prev, advance_payment: e.target.value }))}
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('notes')}</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-none focus:outline-none focus:ring-1 focus:ring-black"
                rows="3"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="reservation-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReservationsPage;
